import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { Select } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import type { CPTCodeResult, CPTCodeSelectOption } from "services/codes-api";
import {
  mapCPTCodeResultToSelectOption,
  useGetCPTCodesByInputQuery,
  useGetDRGCodesByInputQuery,
  useGetHCPCSCodesByInputQuery,
  useGetICDCodesByInputQuery,
} from "services/codes-api";
import { useToken } from "utils/context";
import { formatCPTCodeOptionLabel } from "utils/cptCodeOptionLabel";

import type { OptionType } from "components/inputs";
import type {
  AddCodePreset,
  SupportedCodeType,
} from "../../utils/codeMetadataUtils";

type AddCodePresetModalProps = {
  isOpen: boolean;
  isSubmitting: boolean;
  initialPreset?: AddCodePreset;
  title?: string;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (preset: AddCodePreset) => Promise<void>;
};

type CodeOption = OptionType & {
  code?: string;
  description?: string;
  indicator?: string;
  icd_indicator?: string;
  is_internal?: boolean;
};

type CodeSearchResult = {
  code?: string;
  description?: string;
  indicator?: string;
  icd_indicator?: string;
  is_internal?: boolean;
};

type CodeTypeOption = OptionType & {
  value: SupportedCodeType;
  icdIndicator?: "0" | "9";
};

type SearchType = "CPT" | "DRG" | "HCPCS" | "ICD";

type CodeQueryData = {
  results?: CodeSearchResult[];
} | null;

type SelectChangeValue =
  | OptionType
  | OptionType[]
  | readonly (OptionType | OptionType[])[]
  | null;

const codeTypeOptions: CodeTypeOption[] = [
  { label: "ICD-10", value: "ICD-10", icdIndicator: "0" },
  { label: "ICD-9", value: "ICD-9", icdIndicator: "9" },
  { label: "CPT", value: "CPT" },
  { label: "HCPCS", value: "HCPCS" },
  { label: "DRG", value: "DRG" },
];

const MAX_SEARCH_RESULTS = 25;

const isSingleOption = (option: SelectChangeValue): option is OptionType => {
  if (!option || Array.isArray(option)) {
    return false;
  }

  return typeof option === "object" && "value" in option;
};

const getCodeTypeOption = (
  option: SelectChangeValue,
): CodeTypeOption | null => {
  if (!isSingleOption(option)) {
    return null;
  }

  return (
    codeTypeOptions.find(
      (codeTypeOption) => codeTypeOption.value === option.value,
    ) || null
  );
};

const getCodeOption = (option: SelectChangeValue): CodeOption | null => {
  if (!isSingleOption(option)) {
    return null;
  }

  return option as CodeOption;
};

/*
collects code type and validated code selection before creating a new ai code entry
uses existing code search endpoints so users pick from known code catalogs
*/
const AddCodePresetModal = ({
  isOpen,
  isSubmitting,
  initialPreset,
  title = "Select Code Preset",
  submitLabel = "Add Code",
  onClose,
  onSubmit,
}: AddCodePresetModalProps) => {
  const { accessToken } = useToken();
  const [selectedType, setSelectedType] = useState<CodeTypeOption | null>(
    codeTypeOptions[0],
  );
  const [selectedCode, setSelectedCode] = useState<CodeOption | null>(null);
  const [codeSearchTerm, setCodeSearchTerm] = useState<string | null>(null);
  const [descSearchTerm, setDescSearchTerm] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (initialPreset) {
      const typeOption =
        codeTypeOptions.find(
          (option) => option.value === initialPreset.codeType,
        ) || codeTypeOptions[0];
      setSelectedType(typeOption);
      setSelectedCode({
        label: `${initialPreset.code} - ${initialPreset.description}`,
        value: initialPreset.code,
        code: initialPreset.code,
        description: initialPreset.description,
      });
      return;
    }

    setSelectedType(codeTypeOptions[0]);
    setSelectedCode(null);
  }, [initialPreset, isOpen]);

  const selectedSearchType: SearchType | null =
    selectedType?.value === "ICD-9" || selectedType?.value === "ICD-10"
      ? "ICD"
      : (selectedType?.value ?? null);

  const debouncedSetCodeSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setCodeSearchTerm(value);
      }, 400),
    [],
  );

  const debouncedSetDescSearchTerm = useMemo(
    () =>
      debounce((value: string | null) => {
        setDescSearchTerm(value);
      }, 400),
    [],
  );

  const { data: cptCodeResults, isFetching: isCPTCodeFetching } =
    useGetCPTCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !codeSearchTerm || selectedSearchType !== "CPT",
      },
    );
  const { data: drgCodeResults, isFetching: isDRGCodeFetching } =
    useGetDRGCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !codeSearchTerm || selectedSearchType !== "DRG",
      },
    );
  const { data: hcpcsCodeResults, isFetching: isHCPCSCodeFetching } =
    useGetHCPCSCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !codeSearchTerm || selectedSearchType !== "HCPCS",
      },
    );
  const { data: icdCodeResults, isFetching: isICDCodeFetching } =
    useGetICDCodesByInputQuery(
      {
        code__istartswith: codeSearchTerm || undefined,
        icd_indicator: selectedType?.icdIndicator || "0",
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !codeSearchTerm || selectedSearchType !== "ICD",
      },
    );

  const { data: cptDescResults, isFetching: isCPTDescFetching } =
    useGetCPTCodesByInputQuery(
      {
        description__icontains: descSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !descSearchTerm || selectedSearchType !== "CPT",
      },
    );
  const { data: drgDescResults, isFetching: isDRGDescFetching } =
    useGetDRGCodesByInputQuery(
      {
        description__icontains: descSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !descSearchTerm || selectedSearchType !== "DRG",
      },
    );
  const { data: hcpcsDescResults, isFetching: isHCPCSDescFetching } =
    useGetHCPCSCodesByInputQuery(
      {
        description__icontains: descSearchTerm || undefined,
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !descSearchTerm || selectedSearchType !== "HCPCS",
      },
    );
  const { data: icdDescResults, isFetching: isICDDescFetching } =
    useGetICDCodesByInputQuery(
      {
        description__icontains: descSearchTerm || undefined,
        icd_indicator: selectedType?.icdIndicator || "0",
        limit: MAX_SEARCH_RESULTS,
      },
      {
        skip: !accessToken || !descSearchTerm || selectedSearchType !== "ICD",
      },
    );

  const codeResultsByType: Record<SearchType, CodeQueryData> = {
    CPT: cptCodeResults || null,
    DRG: drgCodeResults || null,
    HCPCS: hcpcsCodeResults || null,
    ICD: icdCodeResults || null,
  };

  const descResultsByType: Record<SearchType, CodeQueryData> = {
    CPT: cptDescResults || null,
    DRG: drgDescResults || null,
    HCPCS: hcpcsDescResults || null,
    ICD: icdDescResults || null,
  };

  const isCodeFetchingByType: Record<SearchType, boolean> = {
    CPT: isCPTCodeFetching,
    DRG: isDRGCodeFetching,
    HCPCS: isHCPCSCodeFetching,
    ICD: isICDCodeFetching,
  };

  const isDescFetchingByType: Record<SearchType, boolean> = {
    CPT: isCPTDescFetching,
    DRG: isDRGDescFetching,
    HCPCS: isHCPCSDescFetching,
    ICD: isICDDescFetching,
  };

  const codeResults = selectedSearchType
    ? codeResultsByType[selectedSearchType]
    : null;
  const descResults = selectedSearchType
    ? descResultsByType[selectedSearchType]
    : null;
  const isCodeFetching = selectedSearchType
    ? isCodeFetchingByType[selectedSearchType]
    : false;
  const isDescFetching = selectedSearchType
    ? isDescFetchingByType[selectedSearchType]
    : false;

  const codeOptions = useMemo(() => {
    if (!codeResults?.results) {
      return [];
    }

    if (selectedSearchType === "CPT") {
      return (codeResults.results as CPTCodeResult[]).map(
        mapCPTCodeResultToSelectOption,
      );
    }

    return codeResults.results.map((result: CodeSearchResult) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code || "",
      description: result.description || "",
      indicator: result.icd_indicator || result.indicator,
    }));
  }, [codeResults, selectedSearchType]);

  const descOptions = useMemo(() => {
    if (!descResults?.results) {
      return [];
    }

    if (selectedSearchType === "CPT") {
      return (descResults.results as CPTCodeResult[]).map(
        mapCPTCodeResultToSelectOption,
      );
    }

    return descResults.results.map((result: CodeSearchResult) => ({
      label: `${result.code} - ${result.description}`,
      value: result.code || "",
      description: result.description || "",
      indicator: result.icd_indicator || result.indicator,
    }));
  }, [descResults, selectedSearchType]);

  const handleCodeSelection = (option: CodeOption | null) => {
    setSelectedCode(option);
  };

  const handleClose = () => {
    setSelectedCode(null);
    setCodeSearchTerm(null);
    setDescSearchTerm(null);
    onClose();
  };

  const handleCreate = async () => {
    if (!selectedType || !(selectedCode?.code ?? selectedCode?.value)) {
      return;
    }

    try {
      await onSubmit({
        codeType: selectedType.value,
        code: String(selectedCode.code ?? selectedCode.value),
        description: selectedCode.description || "",
      });
      handleClose();
    } catch {
      return;
    }
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button
            disabled={!selectedCode || isSubmitting}
            onClick={handleCreate}
          >
            {isSubmitting ? "Saving..." : submitLabel}
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading={title}>
        <Select
          name="ai-code-type"
          label="Code Type"
          value={selectedType}
          options={codeTypeOptions}
          isClearable={false}
          onChange={(option) => {
            const nextSelectedType = getCodeTypeOption(option);
            if (!nextSelectedType) {
              return;
            }

            setSelectedType(nextSelectedType);
            setSelectedCode(null);
            setCodeSearchTerm(null);
            setDescSearchTerm(null);
          }}
        />
        <Select
          name="ai-code-value"
          label="Code"
          placeholder="Search code"
          value={selectedCode}
          options={codeOptions}
          formatOptionLabel={(option: CodeOption, meta) =>
            formatCPTCodeOptionLabel(
              option as CPTCodeSelectOption,
              meta,
              selectedSearchType === "CPT",
              "stacked",
            )
          }
          onInputChange={(value: string) => debouncedSetCodeSearchTerm(value)}
          onChange={(option) => {
            handleCodeSelection(getCodeOption(option));
          }}
          noOptionsMessage={({ inputValue }) =>
            !inputValue
              ? "Begin typing a code..."
              : isCodeFetching
                ? "Searching..."
                : "No matches found."
          }
        />
        <Select
          name="ai-code-description"
          label="Description"
          placeholder="Search description"
          value={
            selectedCode
              ? {
                  ...selectedCode,
                  label: selectedCode.description || selectedCode.label,
                }
              : null
          }
          options={descOptions}
          formatOptionLabel={(option: CodeOption, meta) =>
            formatCPTCodeOptionLabel(
              option as CPTCodeSelectOption,
              meta,
              selectedSearchType === "CPT",
              "stacked",
            )
          }
          onInputChange={(value: string) => debouncedSetDescSearchTerm(value)}
          onChange={(option) => {
            handleCodeSelection(getCodeOption(option));
          }}
          noOptionsMessage={({ inputValue }) =>
            !inputValue
              ? "Begin typing a description..."
              : isDescFetching
                ? "Searching..."
                : "No matches found."
          }
        />
      </Card>
    </KaboodleModal>
  );
};

export default AddCodePresetModal;
